import { useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router';
import { describeApiError, isReportable } from '@/api/errorMessages';
import type { StoryDetail } from '@/api/generated';
import { useChapters, usePlayGate, useStory, useStoryState } from '@/api/queries';
import { CharacterPanel } from '@/features/character/CharacterPanel';
import { openFeedback } from '@/features/feedback/store';
import { ReadingSettings } from '@/features/settings/ReadingSettings';
import { DeathBeat } from '@/features/story/DeathBeat';
import { Journal } from '@/features/story/Journal';
import { StoryFeed } from '@/features/story/StoryFeed';
import { useOpenAtEnd } from '@/features/story/useOpenAtEnd';
import { useReaderPinning } from '@/features/story/useStickToBottom';
import { toReadingOrder } from '@/features/story/readingOrder';
import { runStats } from '@/features/story/runStats';
import { ActionBar } from '@/features/turn/ActionBar';
import { LiveScene } from '@/features/turn/LiveScene';
import { PhaseLine } from '@/features/turn/PhaseLine';
import { TurnAnnouncer } from '@/features/turn/TurnAnnouncer';
import { TurnFailure } from '@/features/turn/TurnFailure';
import { WorldPanel } from '@/features/world/WorldPanel';
import { useT } from '@/i18n/context';
import { I18nProvider } from '@/i18n/I18nProvider';
import { useTurn, useTurnClock } from '@/turn/useTurn';
import { useStickyFootHeight } from '@/ui/stickyFoot';

/**
 * `/play/:storyId` — the story feed and the turn (the story feed–6.2).
 *
 * **This is the file where the live turn's central claim is either true or false**, so it is worth
 * being able to check it by reading: *a running turn must not lock the interface.* There is no
 * `isLoading` here, no overlay, no `<fieldset disabled>`. The turn's state reaches exactly two
 * places — the live scene at the foot of the feed, and whether the action bar is mounted at
 * all — and every other pixel on the page behaves during those fifty seconds exactly as it
 * does at rest: the story scrolls, the journal opens, the panels read, the settings apply.
 * One global flag is how an app loses its whole interface to a single request.
 *
 * **The action bar is not disabled during a turn; it is not there.** It used to sit at the
 * foot greyed out, with the text box still live so the next action could be composed during
 * the wait — a defensible idea that the wait itself disproved. There is nothing to act *on*
 * until the chapter lands, the choices on the bar belong to the chapter that has just been
 * superseded, and a row of dead controls under a paragraph the reader is trying to read is
 * furniture that says only "you cannot". Taking it away says the same thing without the noise
 * and gives the prose the bottom of the screen for the fifty seconds it is being written in.
 * What stays is the feedback button, which is global, and the
 * character panel, whose vitals move *with* the turn. `showLive` is the condition, so the bar
 * comes back on the same frame the chapter commits — or the moment a failure is a failure
 * rather than a turn that may still be running, which is exactly when acting again is legal.
 *
 * The one state that still renders a *disabled* bar is a turn running on a **different**
 * story: nothing of it is on this screen to explain itself, so the bar stays and says why.
 *
 * The live turn renders *inside* the feed rather than beside it, as `children`. That is what
 * makes the commit invisible: the streaming chapter is already in the position, and the width,
 * and the shell (`Scene`) that the committed chapter will occupy a frame later.
 *
 * **Death is a rendering decision made here**, and it is one line: `ending = dead &&
 * !showLive`. The `death` event arrives before `chapter`, so a client that reacted to it would
 * interrupt the final paragraph — the anti-pattern the death-beat brief names explicitly. Waiting for the commit
 * means waiting for the narration to be complete, by definition and without a timer. Death is
 * also the permanent version of what a turn does to the action bar: read-only is expressed by
 * the controls not existing rather than by them being disabled, and a dead story never gets
 * them back.
 *
 * That one flag now drives the whole sequence: the card, the run's four numbers assembled here
 * out of the two requests this screen already made (`runStats`), and the `spent` class that
 * takes the colour out of the scrollback behind it.
 *
 * **The route is split in two so the story can own the language.** The outer half resolves
 * `storyId` into a story; the inner half renders under an `I18nProvider` on `story.locale`,
 * so every string on this screen — the panel headings, the codex badges, the action bar, the
 * suggestion buttons that write a sentence for the DM to read — is in the language the story
 * was created in, whatever the player has since set in settings. A story's language is fixed
 * for its life and there is no way to change it; the interface around it has to agree, or a
 * `de` reader who switched to `en` gets an English frame around German prose.
 */
export function PlayRoute() {
  const { storyId } = useParams<{ storyId: string }>();
  const t = useT();
  const story = useStory(storyId);

  if (storyId === undefined) return <Navigate to="/" replace />;

  // Before the story answers there is no story locale to render in, so these two read in the
  // player's own — which is the only honest answer while the question is still open.
  if (story.isPending) return <Notice>{t('ui.turn.loading_story')}</Notice>;
  if (story.error) return <Notice>{describeApiError(t, story.error)}</Notice>;

  return (
    <I18nProvider locale={story.data.locale}>
      <StoryScreen storyId={storyId} story={story.data} />
    </I18nProvider>
  );
}

function StoryScreen({ storyId, story }: { storyId: string; story: StoryDetail }) {
  const t = useT();
  const location = useLocation();

  const chapters = useChapters(storyId);
  // Slice 1 deleted this fetch — the chapter rows carry their own dateline now (U4), so the
  // feed needs nothing from the world snapshot. The character panel is what brings it back,
  // and it is never awaited: the panel renders its own "reading the state…" and the feed does
  // not wait on a request it has no use for.
  const state = useStoryState(storyId);

  const turn = useTurn(storyId);
  const clock = useTurnClock();

  // Publishes the sticky foot's measured height so the global feedback button can clear it on
  // a phone, where the foot is the only chrome and its height moves with the choice chips.
  const footRef = useStickyFootHeight();

  /**
   * Whether the reader is at the bottom of the story, sampled on their own scroll events for
   * as long as this screen is on it.
   *
   * **It lives here rather than in `LiveScene` because of when it has to be true.** A live turn
   * mounts ~256px of chapter heading, echoed action, empty paragraph and cursor, so by the time
   * the component that follows the narration exists, the reader who was sitting at the bottom
   * is measurably not at it any more; a hook that sampled on its own mount recorded *that*, and
   * the feed followed nobody. This is mounted before the turn and outlives it, so the answer it
   * hands down is about the reader.
   *
   * Gated on the feed being on screen: the first commit of this component renders "Loading the
   * story…", a document shorter than the viewport where everyone is trivially pinned.
   */
  const pinned = useReaderPinning(chapters.isSuccess);

  /**
   * Where the reader *starts*, which is the end of the story and not the top of it. A story is
   * a log; the newest chapter is the one being read. It runs before the first paint, and
   * `CHAPTER_PAGE_SIZE` is small precisely because everything above that landing point is
   * scrollback the reader may never ask for.
   */
  useOpenAtEnd(chapters.isSuccess);

  const pages = chapters.data?.pages;
  const ordered = useMemo(() => toReadingOrder(pages ?? []), [pages]);

  /**
   * The opening turn, when the player arrived from character creation.
   *
   * `POST /stories` and the first turn are two calls (P4), and this is the seam between them:
   * creation navigates here with `autoStart` in the router's state and this fires the turn
   * `runTurn(null)` that the DM answers with chapter 1. The alternative — starting the turn on
   * the creation screen — would hand a fifty-second stream to a component that is about to
   * unmount, and would render it nowhere.
   *
   * Three guards, each closing a different way to spend $0.13 twice:
   *
   * - `started` latches, so a re-render, a StrictMode double-effect or a failed turn the
   * player is looking at cannot fire a second one.
   * - `chapter_count === 0` means it cannot fire on a story that already began — including on
   * a reload of this URL, where the router's history state survives.
   * - `canSubmit` is the turn machine's own rule: no turn of ours is running.
   *
   * A story whose creation succeeded but whose opening turn failed lands here with
   * `chapter_count === 0` and no auto-start, and the action bar's "wake up" is exactly the
   * right control for it.
   */
  const autoStart = (location.state as { autoStart?: boolean } | null)?.autoStart === true;
  const chapterCount = story.chapter_count;
  const canSubmit = turn.canSubmit;
  const gate = usePlayGate();
  const submit = turn.submit;
  const started = useRef(false);

  useEffect(() => {
    if (!autoStart || started.current) return;
    if (chapterCount !== 0 || !canSubmit) return;
    started.current = true;
    submit(null);
  }, [autoStart, chapterCount, canSubmit, submit]);

  if (chapters.isPending) return <Notice>{t('ui.turn.loading_story')}</Notice>;
  if (chapters.error) return <Notice>{describeApiError(t, chapters.error)}</Notice>;

  const dead = story.status === 'dead';
  const streaming = turn.status === 'streaming';

  /**
   * Keep the streaming chapter on screen while it may still land, and take it away the moment
   * it provably did not. The distinction is `isResolving` (`mayStillBeRunning`), and it is the
   * difference between the two failure scenarios: `!interrupt` lost the connection to a turn
   * the server is still finishing, so the half-written scene is still the truth; `!error`
   * aborted, wrote nothing, and leaving its `change` rows on screen would show the player
   * damage they did not take.
   */
  const showLive =
    streaming || turn.status === 'succeeded' || (turn.status === 'failed' && turn.isResolving);

  /**
   * One invariant, stated as one expression: **whatever the ticker is showing, the bars have
   * applied.** `showLive` is already the condition for the streaming scene and its ticker
   * being on screen, so passing the same flag to the panel means the two can never disagree
   * about whether a turn's damage has happened yet. When no turn is live, the newest committed
   * chapter's changes drive the changed-this-chapter dots and nothing else — the snapshot already holds them.
   */
  const newest = ordered.at(-1);
  const panelChanges = showLive ? turn.changes : (newest?.changes ?? []);

  /**
   * **The ending is on screen**, which is a different question from "the character is dead".
   * The `death` event arrives before `chapter`, so between the two the story is over and the
   * last paragraph is still arriving; `!showLive` is what waits for it. Two things read this —
   * the card, and the class that greys out everything above it — and they are the same moment
   * by construction rather than by two conditions that happen to agree today.
   */
  const ending = dead && !showLive;

  /**
   * The panels are the one place a failed request had nowhere to be said. `/state` is
   * fetched but never awaited — the feed has no use for it — so a 500 left both drawers
   * reading *"Reading the state…"* for as long as the player kept them open, which is a
   * spinner that resolves never. Null while it is genuinely pending.
   */
  const stateNote = state.error ? describeApiError(t, state.error) : null;

  /**
   * The journal's summaries are written by a background job the client never hears about, so
   * opening the drawer is when it goes and looks (`Journal`'s `onOpen`). Skipped while a turn
   * is streaming: a refetch that lands after `commitTurn` has prepended the finished chapter
   * would drop it back out of the list until the next one, and a summary is not worth that.
   */
  /**
   * The world half of `/state`, fetched again every time the codex opens.
   *
   * Same shape and same reason as `refreshJournal` below: the commit patches caches and never
   * invalidates them, so a client that never asks again shows a codex one turn behind. The
   * character half is patched by `commitTurn` and is already current; this request refreshes
   * both, which costs one round trip on a modal nobody is watching.
   */
  const refreshCodex = (): void => {
    void state.refetch();
  };

  const refreshJournal = (): void => {
    if (streaming) return;
    void chapters.refetch();
  };

  /**
   * The spoken half of the turn. Assembled here because the pieces live in two places —
   * the DM's phase label is the store's, the stall is the clock's — and the announcer is
   * deliberately given words rather than state to interpret.
   */
  const spokenStatus = streaming
    ? [
        turn.phase?.label ?? t('ui.turn.reading_action'),
        clock.stalled ? t('ui.turn.still_working') : null,
      ]
        .filter((part) => part !== null)
        .join(' — ')
    : null;

  return (
    <main className="flex min-h-dvh flex-col">
      {/*
       * The keyboard path starts here. Four controls sit above the story in DOM order, and
       * without this a keyboard or switch user tabs through every one of them on every page
       * load before reaching the thing they came for. Visible only when focused — it is a
       * shortcut for the people who need it, not a banner.
       */}
      <a
        href={`#${FEED_ID}`}
        className="border-accent bg-panel text-accent sr-only font-mono focus:not-sr-only focus:fixed focus:top-4 focus:left-1/2 focus:z-40 focus:-translate-x-1/2 focus:border focus:px-4 focus:py-2 focus:no-underline"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('ui.a11y.skip_to_story')}
      </a>

      <TurnAnnouncer narration={turn.narration} complete={!streaming} status={spokenStatus} />

      {/*
       * The way out of the story, and the only navigation this screen has. No `z-` on the
       * cluster on purpose: a positioned element with `z-index: auto` creates no stacking
       * context, so the journal's own drawer still renders above the character panel's toggle
       * instead of being trapped under it.
       *
       * That reasoning was right and it was not enough. `backdrop-filter` creates a stacking
       * context too, and `sm:backdrop-blur-none` — the obvious way to turn it off above `sm` —
       * does not: Tailwind v4 compiles it to `--tw-backdrop-blur: " "` plus the same
       * `backdrop-filter: var(…)` chain. So this element was a stacking context at every width,
       * the world and journal drawers were trapped in it, and the sticky foot below painted the
       * action bar over both of them. `sm:backdrop-filter-none` is the reset that emits a
       * literal `backdrop-filter: none`, and it costs nothing here because the bar is
       * `sm:bg-transparent` anyway — there is no backdrop above `sm` worth blurring.
       *
       * `Dialog` portals to the body now, so nothing *depends* on the line below being right.
       * It is corrected so the next drawer written into this cluster does not inherit the trap.
       */}
      <nav
        aria-label={t('ui.a11y.story_controls')}
        /*
         * Two placements, one element. Above `sm` it is a cluster in the corner, out of
         * the way of a wide story column. Below it there is no corner to spare, so it becomes
         * a full-width bar across the top — `overflow-x-auto` because four control words in
         * German are wider than a 390-pixel phone, and a row the player can push sideways is
         * better than one that clips its last control off the screen.
         */
        className="bg-bg/85 fixed top-0 right-0 left-0 flex items-center justify-end gap-1.5 overflow-x-auto px-3 py-2 backdrop-blur sm:top-4 sm:right-4 sm:left-auto sm:gap-2 sm:overflow-x-visible sm:bg-transparent sm:p-0 sm:backdrop-filter-none"
      >
        <Link
          to="/stories"
          className="border-border bg-bg/90 text-fg-muted hover:border-accent hover:text-accent z-20 shrink-0 border px-3 py-1.5 font-mono backdrop-blur transition-colors"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {t('ui.nav.stories')}
        </Link>
        <ReadingSettings />
        <WorldPanel
          world={state.data?.world ?? null}
          // The codex's "here, now" block is the one thing in the panel that needs a fact from
          // the *character* half of `/state`: the snapshot lists every place and says nothing
          // about which one is underfoot.
          here={state.data?.character.location ?? null}
          note={stateNote}
          // U6. `commitTurn` marks the world stale without refetching it, so without this the
          // person the DM just introduced — the link the player is most likely to tap — is
          // missing from the snapshot the panel draws.
          onOpen={refreshCodex}
        />
        <Journal chapters={ordered} onOpen={refreshJournal} />
      </nav>

      <div id={FEED_ID} tabIndex={-1} className="grow pt-10 outline-none sm:pt-0">
        <StoryFeed
          pages={chapters.data.pages}
          older={{
            has: chapters.hasNextPage,
            loading: chapters.isFetchingNextPage,
            load: () => void chapters.fetchNextPage(),
          }}
          // The same condition as the card below, for the same reason: the scrollback goes
          // grey on the frame the ending appears, and not while the last paragraph is still
          // being written.
          spent={ending}
        >
          {showLive ? (
            <LiveScene
              chapterNumber={turn.chapterNumber}
              action={turn.pendingAction}
              narration={turn.narration}
              changes={turn.changes}
              pinned={pinned}
            />
          ) : null}

          {streaming ? (
            <PhaseLine
              label={turn.phase?.label ?? null}
              elapsedMs={clock.elapsedMs}
              stalled={clock.stalled}
            />
          ) : null}

          {turn.status === 'failed' && turn.error ? (
            <TurnFailure
              error={turn.error}
              recovery={turn.recovery}
              resolving={turn.isResolving}
              onRetry={() => {
                turn.submit(turn.pendingAction);
              }}
              onDismiss={turn.dismiss}
              // Only when there is a trace to report and the failure is ours (`isReportable`):
              // a player out of story slots has nothing to send, and P5's request id is what
              // turns the report into a log query.
              onReport={
                isReportable(turn.error)
                  ? () => {
                      openFeedback({
                        category: 'bug',
                        storyId,
                        requestId: turn.error?.requestId ?? null,
                        prefill: `The turn failed: ${describeApiError(t, turn.error)}`,
                      });
                    }
                  : null
              }
            />
          ) : null}

          {/*
            The ending, at the foot of the feed and after the last chapter — which is where the
            reader already is, so nothing has to move them and nothing overlays the paragraph
            they are finishing. `!showLive` is what makes it wait for the prose.
          */}
          {ending ? (
            <DeathBeat
              characterName={story.character_name}
              epitaph={story.cause_of_death ?? t('ui.death.no_epitaph')}
              stats={runStats({
                chapterCount: story.chapter_count,
                last: newest,
                world: state.data?.world ?? null,
              })}
            />
          ) : null}
        </StoryFeed>
      </div>

      {/*
       * The foot of the screen, and on a phone the only chrome that is always visible.
       * The character panel is *here* rather than beside the feed because its mobile half is
       * the peek row — the vitals strip that has to sit directly above the action bar, in
       * flow, so the two cannot overlap. Its desktop chip is `fixed` and does not care where
       * in the tree it is written.
       *
       * Deliberately no `z-index`: `sticky` is a positioned element, so it already paints
       * above the static feed, and giving it a layer would create a stacking context that
       * traps the character sheet's own `z-30` inside it.
       */}
      <div ref={footRef} className="sticky bottom-0">
        <CharacterPanel
          character={state.data?.character ?? null}
          time={state.data?.world.time ?? null}
          changes={panelChanges}
          pending={showLive}
          note={stateNote}
        />

        {/*
         * `dead || showLive` — the two ways there is nothing to act with. Death is permanent
         * and a turn is fifty seconds, but the reason is the same one and so is the answer:
         * the controls are absent rather than disabled. See the header of this file.
         */}
        {dead || showLive ? null : (
          <ActionBar
            choices={story.choices}
            canSubmit={turn.canSubmit && gate?.canPlay !== false}
            /*
             * Two states reach this, and both are off-screen causes — which is the whole
             * reason this bar says them itself. A turn resolving on *another* story holds
             * this client's one turn slot; a play gate (`usePlayGate`) means the account is
             * unactivated or out of budget, which the server would answer with a 403 or a
             * 429 the moment the player pressed anything. The gate wins the sentence,
             * because it is the one that will not clear on its own in fifty seconds.
             */
            disabledReason={t(gate?.reason ?? 'ui.turn.in_progress')}
            opening={story.chapter_count === 0}
            onSubmit={turn.submit}
          />
        )}
      </div>
    </main>
  );
}

/**
 * The skip link's target. `tabIndex={-1}` so focus can actually land on it — a fragment jump
 * moves the viewport but not the focus ring unless the target is focusable, which is the half
 * of a skip link that is usually missing.
 */
const FEED_ID = 'story-feed';

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-[var(--measure)] flex-col justify-center gap-4 p-8">
      <p className="text-fg-muted" style={{ fontSize: 'var(--text-meta)' }}>
        {children}
      </p>
    </main>
  );
}
